<template>
  <!-- Mobile layout -->
  <transition-group
    v-if="variant === 'mobile'"
    name="stagger"
    tag="div"
    class="mt-0 w-full flex flex-col items-center space-y-6 pt-6 px-4"
    appear
  >
    <!-- 1. Select Region (International Only) -->
    <div v-if="isInternational" key="region" class="w-full">
      <h3 class="text-lg font-normal text-center mb-6">Select Region</h3>
      <div class="grid grid-cols-2 gap-2 justify-center px-0">
        <button
          v-for="region in regionOptions"
          :key="region.id"
          @click="toggleRegion(region.id)"
          :class="[
            'w-full px-4 h-[44px] flex items-center justify-center rounded-full text-sm font-semibold transition-all border',
            selectedRegions.includes(region.id)
              ? 'bg-gray-100 text-[#2F3033] border-gray-200'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          ]"
        >
          {{ region.label }}
        </button>
      </div>
      <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4 mt-6"></div>
    </div>

    <!-- 2. Trip vibe -->
    <div key="trip-type-discover" class="w-full">
      <h3 class="text-lg font-normal text-center mb-6">Select trip vibe</h3>
      <div class="grid grid-cols-4 gap-[10px] px-0">
        <button
          v-for="type in tripTypeOptionsDiscover"
          :key="type.id"
          @click="toggleTripTypeDiscover(type.id)"
          :class="[
            'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
            selectedTripTypesDiscover.includes(type.id)
              ? 'border-gray-200 bg-gray-100'
              : 'border-gray-200 bg-white hover:bg-gray-50'
          ]"
        >
          <component :is="type.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
          <span class="text-xs font-semibold text-center leading-tight">{{ type.label }}</span>
        </button>
      </div>
      <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4 mt-6"></div>
    </div>

    <!-- 3. Who is going -->
    <div key="who-going-discover" class="w-full">
      <h3 class="text-lg font-normal text-center mb-6">Who is going?</h3>
      <div class="grid grid-cols-3 gap-[10px] justify-center px-0">
        <button
          v-for="who in whoIsGoingOptions"
          :key="who.id"
          @click="selectWhoIsGoing(who.id)"
          :class="[
            'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
            selectedWhoIsGoing === who.id
              ? 'border-gray-200 bg-gray-100'
              : 'border-gray-200 bg-white hover:bg-gray-50'
          ]"
        >
          <component :is="who.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
          <span class="text-xs font-semibold text-center leading-tight">{{ who.label }}</span>
        </button>
      </div>
      <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4 mt-6"></div>
    </div>

    <!-- 4. Activities -->
    <div key="activities-discover" class="w-full">
      <h3 class="text-lg font-normal text-center mb-6">Add preferred activities</h3>
      <div class="w-full">
        <TripfinderKeywordTaginput
          v-model="selectedActivities"
          :max-tags="15"
          hide-clear-all
        />
      </div>
      <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4 mt-6"></div>
    </div>

    <!-- 5. Travel distance -->
    <div key="travel-distance" class="w-full pb-4">
      <h3 class="text-lg font-normal text-center mb-6">Set travel distance</h3>
      <RefineDistanceSlider
        :model-value="travelDistanceValue"
        :selected-distance-type="selectedDistanceType"
        :is-international="isInternational"
        variant="mobile"
        @update:model-value="travelDistanceValue = $event"
        @update:selected-distance-type="selectedDistanceType = $event"
      />
    </div>
  </transition-group>

  <!-- Desktop: International layout -->
  <div v-else-if="isInternational" class="flex flex-col w-full pl-0 pr-4">
    <!-- Full-width: Select Region -->
    <div class="w-full mb-4 pt-10 relative">
      <div class="bg-gray-50 border border-gray-100 p-5 pt-8 rounded-[32px] relative">
        <div class="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2F3033] text-white px-6 py-1 rounded-full text-sm font-bold shadow-sm">
          Select Region
        </div>
        <div class="flex flex-wrap gap-2 justify-center px-0">
          <button
            v-for="region in regionOptions"
            :key="region.id"
            @click="toggleRegion(region.id)"
            :class="[
              'px-4 h-[44px] flex items-center justify-center rounded-full text-sm font-semibold transition-all border',
              selectedRegions.includes(region.id)
                ? 'bg-gray-100 text-[#2F3033] border-gray-200'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            ]"
          >
            {{ region.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="w-full h-[1px] bg-gray-200 mb-6"></div>

    <!-- Four-column grid -->
    <div class="grid grid-cols-1 md:grid-cols-12 gap-[20px]">
      <!-- Col 1: Trip vibe -->
      <div class="relative md:col-span-3">
        <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
        <div>
          <h3 class="text-base font-semibold text-center mb-4">Select trip vibe</h3>
          <div class="grid grid-cols-3 gap-[10px] px-0">
            <button
              v-for="type in tripTypeOptionsDiscover"
              :key="type.id"
              @click="toggleTripTypeDiscover(type.id)"
              :class="[
                'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
                selectedTripTypesDiscover.includes(type.id)
                  ? 'border-gray-200 bg-gray-100'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              ]"
            >
              <component :is="type.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
              <span class="text-xs font-semibold text-center leading-tight">{{ type.label }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Col 2: Activities -->
      <div class="relative md:col-span-3">
        <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
        <div>
          <div class="relative flex items-center justify-center mb-4">
            <h3 class="text-base font-semibold">Add preferred activities</h3>
            <button
              v-if="selectedActivities.length > 0"
              @click="clearActivities"
              class="absolute right-4 text-sm text-[#2F3033] hover:text-[#2F3033] underline transition-colors md:hidden"
            >
              Clear
            </button>
          </div>
          <div class="w-full relative">
            <TripfinderKeywordTaginput
              v-model="selectedActivities"
              :max-tags="15"
              desktop-style
              hide-clear-all
            />
            <div class="flex justify-end mt-2">
              <button
                v-if="selectedActivities.length > 0"
                @click="clearActivities"
                class="hidden md:block text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
              >
                Clear activities
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Col 3: Who is going -->
      <div class="relative md:col-span-3">
        <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
        <div>
          <h3 class="text-base font-semibold text-center mb-4">Who is going?</h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-[10px] justify-center px-0">
            <button
              v-for="who in whoIsGoingOptions"
              :key="who.id"
              @click="selectWhoIsGoing(who.id)"
              :class="[
                'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
                selectedWhoIsGoing === who.id
                  ? 'border-gray-200 bg-gray-100'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              ]"
            >
              <component :is="who.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
              <span class="text-xs font-semibold text-center leading-tight">{{ who.label }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Col 4: Travel distance -->
      <div class="relative md:col-span-3">
        <div>
          <h3 class="text-base font-semibold text-center mb-4">Set travel distance</h3>
          <RefineDistanceSlider
            :model-value="travelDistanceValue"
            :selected-distance-type="selectedDistanceType"
            :is-international="true"
            variant="desktop"
            @update:model-value="travelDistanceValue = $event"
            @update:selected-distance-type="selectedDistanceType = $event"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop: Local Discover layout -->
  <div v-else class="grid grid-cols-1 md:grid-cols-12 gap-[20px] pl-0 pr-4">
    <!-- Col 1: Trip vibe -->
    <div class="relative space-y-6 md:col-span-3">
      <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
      <div>
        <h3 class="text-base font-semibold text-center mb-6 md:mt-6">Select trip vibe</h3>
        <div class="grid grid-cols-3 gap-[10px] px-0">
          <button
            v-for="type in tripTypeOptionsDiscover"
            :key="type.id"
            @click="toggleTripTypeDiscover(type.id)"
            :class="[
              'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
              selectedTripTypesDiscover.includes(type.id)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            ]"
          >
            <component :is="type.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
            <span class="text-xs font-semibold text-center leading-tight">{{ type.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Col 2: Who is going -->
    <div class="relative space-y-6 md:col-span-3">
      <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
      <div>
        <h3 class="text-base font-semibold text-center mb-6 md:mt-6">Who is going?</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-[10px] justify-center px-0">
          <button
            v-for="who in whoIsGoingOptions"
            :key="who.id"
            @click="selectWhoIsGoing(who.id)"
            :class="[
              'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
              selectedWhoIsGoing === who.id
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            ]"
          >
            <component :is="who.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
            <span class="text-xs font-semibold text-center leading-tight">{{ who.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Col 3: Activities -->
    <div class="relative space-y-6 md:col-span-3">
      <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
      <div>
        <div class="relative flex items-center justify-center mb-6">
          <h3 class="text-base font-semibold md:mt-1">Add preferred activities</h3>
          <button
            v-if="selectedActivities.length > 0"
            @click="clearActivities"
            class="absolute right-4 text-sm text-[#2F3033] hover:text-[#2F3033] underline transition-colors md:hidden"
          >
            Clear
          </button>
        </div>
        <div class="w-full relative">
          <TripfinderKeywordTaginput
            v-model="selectedActivities"
            :max-tags="15"
            desktop-style
            hide-clear-all
          />
          <div class="flex justify-end mt-2">
            <button
              v-if="selectedActivities.length > 0"
              @click="clearActivities"
              class="hidden md:block text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
            >
              Clear activities
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Col 4: Travel distance -->
    <div class="relative space-y-6 md:col-span-3">
      <div>
        <h3 class="text-base font-semibold text-center mb-6 md:mt-6">Set travel distance</h3>
        <RefineDistanceSlider
          :model-value="travelDistanceValue"
          :selected-distance-type="selectedDistanceType"
          :is-international="false"
          variant="desktop"
          @update:model-value="travelDistanceValue = $event"
          @update:selected-distance-type="selectedDistanceType = $event"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRefineStore } from '@/store/refine.js'
import { regionOptions, tripTypeOptionsDiscover, whoIsGoingOptions } from '@/config/tripFinderOptions.js'
import TripfinderKeywordTaginput from './TripfinderKeywordTaginput.vue'
import RefineDistanceSlider from './RefineDistanceSlider.vue'

defineOptions({ name: 'RefineContentDiscover' })

const props = defineProps({
  variant: { type: String, default: 'mobile' }, // 'mobile' | 'desktop'
  isInternational: { type: Boolean, default: false }
})

const refine = useRefineStore()

const selectedRegions = computed(() => refine.selectedRegions)
const selectedTripTypesDiscover = computed(() => refine.selectedTripTypesDiscover)
const selectedWhoIsGoing = computed(() => refine.selectedWhoIsGoing)
const selectedActivities = computed({
  get: () => refine.selectedActivities,
  set: (val) => refine.selectedActivities = val
})
const travelDistanceValue = computed({
  get: () => refine.travelDistanceValue,
  set: (val) => refine.travelDistanceValue = val
})
const selectedDistanceType = computed({
  get: () => refine.selectedDistanceType,
  set: (val) => refine.selectedDistanceType = val
})

const toggleRegion = (id) => {
  if (id === 'anywhere') {
    refine.selectedRegions = ['anywhere']
    return
  }
  let current = [...refine.selectedRegions]
  if (current.includes('anywhere')) current = []
  const index = current.indexOf(id)
  if (index > -1) {
    current.splice(index, 1)
  } else {
    current.push(id)
  }
  refine.selectedRegions = current
}

const toggleTripTypeDiscover = (id) => {
  const current = [...refine.selectedTripTypesDiscover]
  const index = current.indexOf(id)
  if (index > -1) {
    current.splice(index, 1)
  } else {
    current.push(id)
  }
  refine.selectedTripTypesDiscover = current
}

const selectWhoIsGoing = (id) => {
  if (refine.selectedWhoIsGoing === id) {
    refine.selectedWhoIsGoing = null
    return
  }
  refine.selectedWhoIsGoing = id
  if (id === 'solo') refine.travelersData = { adults: 1, children: 0, pets: 0 }
  else if (id === 'friends') refine.travelersData = { adults: 4, children: 0, pets: 0 }
  else if (id === 'couple') refine.travelersData = { adults: 2, children: 0, pets: 0 }
  else if (id === 'family') refine.travelersData = { adults: 2, children: 2, pets: 0 }
  else if (id === 'furry-friends') refine.travelersData = { adults: 1, children: 0, pets: 1 }
}

const clearActivities = () => { refine.selectedActivities = [] }
</script>

<style scoped>
.stagger-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.stagger-enter-active {
  transition: all 0.4s ease-out;
}
.stagger-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.stagger-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.stagger-leave-active {
  transition: all 0.3s ease-in;
}
.stagger-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
